import React, { useEffect, useMemo, useState } from 'react';
import XtramModal, { XtramButton, XtramField, XtramNotice, XtramSelect } from '../../Widgets/XtramModal/XtramModal.jsx';
import FilterMonthYearPicker from '../../Widgets/CustomDates/FilterMonthYearPicker/FilterMonthYearPicker.jsx';
import headerStyles from '../../ClientDetails/SubComponents/ClientDetailsHeader/HeaderToolbar.module.scss';
import styles from './OtherDocumentsXtram.module.scss';

// Mid-month UTC dates keep the month intact through toISOString in every timezone.
const monthOf = (date) => {
    const value = date ? new Date(date) : new Date();
    return new Date(Date.UTC(value.getFullYear(), value.getMonth(), 10));
};

// FilterMonthYearPicker emits UTC Jan 1 for a new year and a UTC month start for a new month.
const pickMonth = (current, picked, part) => {
    const next = new Date(picked);
    return part === 'year'
        ? new Date(Date.UTC(next.getUTCFullYear(), current.getUTCMonth(), 10))
        : new Date(Date.UTC(current.getUTCFullYear(), next.getUTCMonth(), 10));
};

/**
 * Zip download of the case's other documents. `onDownload({ documentTypes, startDate, endDate })`
 * resolves to true on success; dates are mid-month UTC dates, or null for every date.
 */
const OtherDocumentDownloadModal = ({
    open,
    onClose,
    onDownload,
    t,
    direction,
    typeOptions = [],
    defaultTypes = [],
    defaultRange,
    busy = false,
}) => {
    const [selected, setSelected] = useState([]);
    const [useRange, setUseRange] = useState(false);
    const [start, setStart] = useState(() => monthOf());
    const [end, setEnd] = useState(() => monthOf());

    const options = useMemo(
        () => typeOptions.filter((type) => type?.name).map((type) => ({ value: type.name, label: type.name })),
        [typeOptions]
    );

    useEffect(() => {
        if (!open) return;
        const preset = defaultTypes.length ? defaultTypes : options.map((option) => option.value);
        setSelected(options.filter((option) => preset.includes(option.value)));
        setUseRange(!!defaultRange?.enabled);
        setStart(monthOf(defaultRange?.start));
        setEnd(monthOf(defaultRange?.end));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const allSelected = options.length > 0 && selected.length === options.length;
    const rangeInvalid = useRange && start > end;
    const canDownload = selected.length > 0 && !rangeInvalid && !busy;

    const handleDownload = async () => {
        if (!canDownload) return;
        const ok = await onDownload({
            documentTypes: selected.map((option) => option.value),
            startDate: useRange ? start : null,
            endDate: useRange ? end : null,
        });
        if (ok) onClose();
    };

    return (
        <XtramModal
            open={open}
            onClose={onClose}
            title={t('DownloadDocuments')}
            direction={direction}
            busy={busy}
            footer={(
                <>
                    <XtramButton variant="secondary" onClick={onClose} disabled={busy}>{t('close')}</XtramButton>
                    <XtramButton onClick={handleDownload} disabled={!canDownload} busy={busy}>{t('Download')}</XtramButton>
                </>
            )}
        >
            <div className={styles.stack}>
                <XtramField label={t('Document Type')} required size="full">
                    <XtramSelect
                        direction={direction}
                        multiple
                        options={options}
                        value={selected}
                        onChange={setSelected}
                        placeholder={t('SelectDocumentTypes')}
                    />
                </XtramField>
                <div className={styles.row}>
                    <XtramButton
                        variant="ghost"
                        small
                        onClick={() => setSelected(allSelected ? [] : options)}
                        disabled={!options.length}
                    >
                        {allSelected ? t('DeselectAll') : t('SelectAll')}
                    </XtramButton>
                    <span className={styles.muted}>
                        {t('OtherDocumentsSelectedCategories', { count: selected.length, total: options.length })}
                    </span>
                </div>

                <label className={`${headerStyles.switchRow} ${headerStyles[direction] || ''}`} dir={direction}>
                    <span>{t('DateRange')}</span>
                    <input type="checkbox" checked={useRange} onChange={() => setUseRange((prev) => !prev)} />
                    <span className={headerStyles.switchTrack} aria-hidden="true" />
                </label>

                {useRange ? (
                    <div className={headerStyles.dateSelectRange}>
                        <div className={headerStyles.dateSelect}>
                            <FilterMonthYearPicker
                                year={start}
                                month={start}
                                onYearChange={(value) => setStart((prev) => pickMonth(prev, value, 'year'))}
                                onMonthChange={(value) => setStart((prev) => pickMonth(prev, value, 'month'))}
                                labelYear={t('StartYear')}
                                labelMonth={t('StartMonth')}
                                direction={direction}
                            />
                        </div>
                        <div className={headerStyles.rangeDivider} />
                        <div className={headerStyles.dateSelect}>
                            <FilterMonthYearPicker
                                year={end}
                                month={end}
                                onYearChange={(value) => setEnd((prev) => pickMonth(prev, value, 'year'))}
                                onMonthChange={(value) => setEnd((prev) => pickMonth(prev, value, 'month'))}
                                labelYear={t('EndYear')}
                                labelMonth={t('EndMonth')}
                                direction={direction}
                            />
                        </div>
                    </div>
                ) : (
                    <span className={styles.muted}>{t('OtherDocumentsDownloadAllDates')}</span>
                )}

                {rangeInvalid && <XtramNotice type="error">{t('PCNStartAfterEnd')}</XtramNotice>}
            </div>
        </XtramModal>
    );
};

export default OtherDocumentDownloadModal;
